import GameObject from '../../../GameObject/GameObject';
import TransformComponent from '../../../Component/Transform';
import AnimationClip from '../AnimationClip';
import BaseCard from '../../../Prototype/Level/Card/BaseCard';
import { Easing } from '@tweenjs/tween.js';
import ZapCard from '../../../Prototype/Level/Card/ZapCard';
import ZapOverlay from '../../../Prototype/Level/Card/ZapOverlay';
import ZapChunk from '../../../Prototype/Level/Card/ZapChunk';
import Renderable from '../../../Component/Renderable';
import Container from '../../../Prototype/Container';
import { delay } from '../../../Utility/Task/TaskQueue';
import {
    GetAnimationClip,
    PlayAnimation,
    StartAnimation,
} from '../AnimationHelper';
import CardSystem from '../../Card/CardSystem';
import { showCardScoreEffect } from '../CardAnimationHelper';

interface ICardZapMetaData {
    system: CardSystem;
    sourceCard: ZapCard;
    targetCards: BaseCard[];
}

export class CardZap extends AnimationClip {
    public *execute(
        animator: GameObject,
        meta: ICardZapMetaData
    ): IterableIterator<number> {
        // Create containers for proper depth sorting.
        const cardsContainer = GameObject.Instantiate(Container);
        const chunksContainer = GameObject.Instantiate(Container);

        animator.addChild(chunksContainer);
        animator.addChild(cardsContainer);

        cardsContainer.addChild(meta.sourceCard);
        meta.targetCards.forEach(card => {
            cardsContainer.addChild(card);
        });

        const sourceCardTransform = meta.sourceCard.getComponent(
            TransformComponent
        );

        // Move the chunks behind the source card.
        chunksContainer
            .getComponent(TransformComponent)
            .position.set(
                sourceCardTransform.position.x,
                sourceCardTransform.position.y
            );

        // Fade in the overlay.
        const overlay = animator.getChildByName('overlay');
        StartAnimation(overlay, 'OverlayFadeIn');

        const scaleTween = this.getTween(sourceCardTransform.scale);
        scaleTween.to({ x: 0.8, y: 0.8 }, 50).easing(Easing.Quadratic.InOut);

        yield* this.startTween(scaleTween);

        scaleTween.to({ x: 1, y: 1 }, 50);
        scaleTween.start(this.elapsedTime);

        const zapOverlay = GameObject.Instantiate(ZapOverlay);
        meta.sourceCard.addChild(zapOverlay);

        yield* delay(200);

        const chunks = 45;
        const chunkWidth = 80;

        for (let i = 0; i < chunks; i++) {
            const chunk = GameObject.Instantiate(ZapChunk, {
                x: -(chunks * chunkWidth * 0.5) + 80 * i,
                y: -45,
            });
            chunksContainer.addChild(chunk);
        }

        for (let i = 0; i < 45; i++) {
            const chunk = GameObject.Instantiate(ZapChunk, {
                x: -(chunks * chunkWidth * 0.5) + 80 * i,
                y: 45,
            });
            chunksContainer.addChild(chunk);
        }

        yield* delay(300);

        //
        //
        //

        const cardsLeft: BaseCard[] = [];
        const cardsRight: BaseCard[] = [];

        meta.targetCards.forEach(card => {
            const transform = card.getComponent(TransformComponent);
            if (transform.x > sourceCardTransform.x) {
                cardsRight.push(card);
            } else {
                cardsLeft.push(card);
            }
        });

        cardsRight.sort(
            (a, b) =>
                a.getComponent(TransformComponent).x -
                b.getComponent(TransformComponent).x
        );

        cardsLeft.sort(
            (a, b) =>
                b.getComponent(TransformComponent).x -
                a.getComponent(TransformComponent).x
        );

        const length = Math.max(cardsRight.length, cardsLeft.length);

        for (let i = 0; i < length; i++) {
            if (cardsLeft[i]) {
                cardsLeft[i].addChild(GameObject.Instantiate(ZapOverlay));
            }

            if (cardsRight[i]) {
                cardsRight[i].addChild(GameObject.Instantiate(ZapOverlay));
            }

            yield* delay(200);
        }

        chunksContainer.destroy();
        zapOverlay.destroy();
        meta.sourceCard.getComponent(Renderable).visible = false;

        showCardScoreEffect(meta.system.scene, meta.sourceCard);

        yield* delay(500);

        for (let i = 0; i < length; i++) {
            if (cardsLeft[i]) {
                cardsLeft[i].getChildByName('zapOverlay').destroy();
                cardsLeft[i].getComponent(Renderable).visible = false;
                showCardScoreEffect(meta.system.scene, cardsLeft[i]);
            }

            if (cardsRight[i]) {
                cardsRight[i].getChildByName('zapOverlay').destroy();
                cardsRight[i].getComponent(Renderable).visible = false;
                showCardScoreEffect(meta.system.scene, cardsRight[i]);
            }

            yield* delay(200);
        }

        // Fade out.
        yield* PlayAnimation(GetAnimationClip('OverlayFadeOut', overlay));
    }
}
